import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { PageHeader } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Paperclip, X, Send, UserCheck } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { StatusPill, SUPPORT_CATEGORY_LABELS, SUPPORT_PRIORITY_STYLES, SUPPORT_STATUS_LABELS } from "@/pages/seller/Support";
import { MessageThread } from "@/pages/seller/SupportDetail";

const STATUSES = ["OPEN", "IN_PROGRESS", "AWAITING_REPLY", "RESOLVED", "CLOSED"];
const MAX_BYTES = 5 * 1024 * 1024;
const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const AdminSupportDetail = ({ user, onLogout }) => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/support/${ticketId}`);
      setTicket(data);
    } catch (e) {
      toast.error(errDetail(e, "Failed to load the ticket"));
      navigate("/admin/support");
    } finally {
      setLoading(false);
    }
  }, [ticketId, navigate]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [ticket?.messages?.length]);

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => {
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds the 5 MB limit`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...picked].slice(0, 5));
    e.target.value = "";
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("body", reply.trim());
      fd.append("internal", internal ? "true" : "false");
      files.forEach((f) => fd.append("attachments", f));
      const { data } = await axios.post(`${API}/admin/support/${ticketId}/reply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTicket(data);
      setReply(""); setFiles([]); setInternal(false);
    } catch (e) {
      toast.error(errDetail(e, "Could not send the reply"));
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/support/${ticketId}/status`, { status });
      setTicket(data);
      toast.success(`Status set to ${SUPPORT_STATUS_LABELS[status] || status}`);
    } catch (e) {
      toast.error(errDetail(e, "Could not change status"));
    } finally {
      setBusy(false);
    }
  };

  const assignToMe = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/support/${ticketId}/assign`, { assignee_user_id: user.id });
      setTicket(data);
      toast.success("Assigned to you");
    } catch (e) {
      toast.error(errDetail(e, "Could not assign"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <AdminLayout user={user} onLogout={onLogout}><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></AdminLayout>;
  }
  if (!ticket) return null;

  const mineAssigned = ticket.assignee?.user_id === user.id;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-5 w-full max-w-4xl">
        <PageHeader
          title={ticket.ticket_number}
          subtitle={ticket.subject}
          backTo="/admin/support"
          badge={<StatusPill status={ticket.status} />}
        />

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 space-y-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span><span className="text-slate-500">Seller:</span> <span className="font-medium text-slate-800">{ticket.seller_brand_name || ticket.seller_id}</span></span>
              <span><span className="text-slate-500">Category:</span> <span className="font-medium text-slate-800">{SUPPORT_CATEGORY_LABELS[ticket.category] || ticket.category}</span></span>
              <span><span className="text-slate-500">Priority:</span> <span className={`font-medium capitalize ${SUPPORT_PRIORITY_STYLES[ticket.priority] || ""}`}>{ticket.priority}</span></span>
              {ticket.order_number && <span><span className="text-slate-500">Order:</span> <span className="font-medium text-slate-800">{ticket.order_number}</span></span>}
              {ticket.awb && <span><span className="text-slate-500">AWB:</span> <span className="font-mono text-slate-700">{ticket.awb}</span></span>}
              <span className="text-slate-400">Raised {formatToIST(ticket.created_at)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Status</span>
                <Select value={ticket.status} onValueChange={changeStatus} disabled={busy}>
                  <SelectTrigger className="h-8 w-[160px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{SUPPORT_STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Assignee</span>
                <span className="text-sm font-medium text-slate-800">{ticket.assignee ? (ticket.assignee.name || ticket.assignee.email) : "Unassigned"}</span>
                {!mineAssigned && <Button variant="outline" size="sm" onClick={assignToMe} disabled={busy}><UserCheck className="w-3.5 h-3.5 mr-1.5" /> Assign to me</Button>}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5">
            <MessageThread messages={ticket.messages} myRole="admin" />
            <div ref={bottomRef} />
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 space-y-3">
            <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={internal ? "Internal note (not visible to the seller)…" : "Reply to the seller…"} />
            {files.length > 0 && (
              <ul className="space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between rounded bg-slate-50 px-3 py-1.5 text-sm">
                    <span className="truncate max-w-[260px]">{f.name}</span>
                    <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm text-slate-600 hover:text-slate-800">
                  <Paperclip className="w-4 h-4" /> Attach
                  <input type="file" multiple accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={onFiles} />
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="rounded border-slate-300" />
                  Internal note (private)
                </label>
              </div>
              <Button onClick={sendReply} disabled={sending || !reply.trim()} className={internal ? "bg-amber-600 hover:bg-amber-700" : ""}>
                {sending ? "Sending…" : <><Send className="w-4 h-4 mr-1.5" /> {internal ? "Add note" : "Send reply"}</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSupportDetail;
